'use client';

// Full derivation modal for the 4-method consensus target price.
// Opens when the user clicks the Consensus FV tile in TargetPriceRow.
// Shows every input and calculation so Steve can verify the math.

interface DcfBreakdown {
  projectedOwnerEarnings?: number[];
  stageOnePvs?: number[];
  stageOneSum?: number;
  terminalValue?: number;
  terminalPv?: number;
  fairValuePerShare?: number;
}
interface CostOfEquityBreakdown {
  riskFreeRate?: number;
  beta?: number;
  equityRiskPremium?: number;
  capmBaseline?: number;
  leveragePremium?: number;
  sizePremium?: number;
  notes?: string[];
}
interface TargetMultiple {
  peerMedian?: number;
  ownMedian?: number;
  final?: number;
  nearTerm?: number;
  isOverridden?: boolean;
}

export interface TargetPriceDerivation {
  symbol: string;
  currentPrice: number | null;
  peerGroup: string;
  // P/E method
  todayFairValue: number | null;
  fy1Eps: number | null;
  targetMultiple: TargetMultiple | null;
  // EV/EBITDA method
  evEbitdaMultiple: number | null;
  evEbitdaFairValue: number | null;
  fwdEbitdaRaw: number | null;
  fwdNetDebtRaw: number | null;
  marketCap: number | null;
  // DCF method
  dcfFairValue: number | null;
  dcfBreakdown: DcfBreakdown | null;
  dcfDiscountRate: number | null;
  costOfEquityBreakdown: CostOfEquityBreakdown | null;
  ownerEarningsPerShare: number | null;
  // P/FCF method
  pfcfMultiple: number | null;
  fcfPerShare: number | null;
  fcfRaw: number | null;
  pfcfFairValue: number | null;
  // Consensus
  consensusFairValue: number | null;
  methodCoverage: number | null;
}

const W = { pe: 0.40, eveb: 0.30, dcf: 0.20, pfcf: 0.10 };

function fmt$(v: number | null | undefined, decimals = 0): string {
  if (v == null || !Number.isFinite(v)) return '—';
  const abs = Math.abs(v);
  if (abs >= 1e12) return `$${(v / 1e12).toFixed(1)}T`;
  if (abs >= 1e9)  return `$${(v / 1e9).toFixed(1)}B`;
  if (abs >= 1e6)  return `$${(v / 1e6).toFixed(1)}M`;
  return `$${v.toFixed(decimals)}`;
}
function fmtPct(v: number | null | undefined, d = 1): string {
  if (v == null || !Number.isFinite(v)) return '—';
  const p = v * 100;
  return `${p >= 0 ? '+' : ''}${p.toFixed(d)}%`;
}
function fmtX(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return `${v.toFixed(1)}×`;
}

function MethodCard({
  label, weight, totalW, fv, current, available, children,
}: { label: string; weight: number; totalW: number; fv: number | null; current: number | null; available: boolean; children: React.ReactNode }) {
  const effectiveW = totalW > 0 ? weight / totalW : weight;
  const contrib = fv != null && available ? fmt$(fv * effectiveW, 2) : '—';
  const pctDelta = fv && current && current > 0 ? (fv / current - 1) * 100 : null;
  const color = pctDelta == null ? '#64748b' : pctDelta >= 0 ? '#10b981' : '#f87171';
  const baseLabel = totalW < 1.0 - 0.001
    ? `base ${(weight * 100).toFixed(0)}% → effective ${(effectiveW * 100).toFixed(0)}%`
    : `weight ${(weight * 100).toFixed(0)}%`;
  return (
    <div className={`rounded-lg border p-3 ${available ? 'border-[#1e3a5f] bg-[#0a1929]' : 'border-[#1e293b] bg-[#0a0e14] opacity-50'}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-bold text-[#94a3b8] uppercase tracking-wider">{label}</div>
        <div className="flex items-center gap-2">
          <span className="text-[9px] text-[#475569]">{baseLabel}</span>
          {fv != null && available && (
            <span className="text-[10px] font-mono" style={{ color }}>
              {fmt$(fv, 2)} {pctDelta != null && `(${pctDelta >= 0 ? '+' : ''}${pctDelta.toFixed(0)}%)`}
            </span>
          )}
          {(!available || fv == null) && <span className="text-[10px] text-[#334155]">N/A</span>}
        </div>
      </div>
      {children}
      {fv != null && available && (
        <div className="mt-2 pt-2 border-t border-[#1e293b] text-[9px] text-[#475569]">
          Contributes {contrib} to consensus ({(effectiveW * 100).toFixed(0)}% × {fmt$(fv, 2)})
        </div>
      )}
    </div>
  );
}

function Row({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2 py-0.5">
      <span className="text-[10px] text-[#64748b]">{label}</span>
      <span className="text-[10px] font-mono text-[#cbd5e1] tabular-nums">
        {value}
        {sub && <span className="text-[9px] text-[#475569] ml-1">{sub}</span>}
      </span>
    </div>
  );
}

function Sep() {
  return <div className="border-t border-[#1e293b] my-1.5" />;
}

export function TargetPriceModal({
  d,
  onClose,
}: { d: TargetPriceDerivation; onClose: () => void }) {
  // Normalize weights to available methods (mirrors compute-target-prices.ts)
  const availWeights: { method: string; w: number; fv: number }[] = [];
  if (d.todayFairValue != null)   availWeights.push({ method: 'pe',   w: W.pe,   fv: d.todayFairValue });
  if (d.evEbitdaFairValue != null) availWeights.push({ method: 'eveb', w: W.eveb, fv: d.evEbitdaFairValue });
  if (d.dcfFairValue != null)      availWeights.push({ method: 'dcf',  w: W.dcf,  fv: d.dcfFairValue });
  if (d.pfcfFairValue != null)     availWeights.push({ method: 'pfcf', w: W.pfcf, fv: d.pfcfFairValue });
  const totalW = availWeights.reduce((s, m) => s + m.w, 0);

  // EV/EBITDA calculation detail
  const sharesApprox = d.currentPrice && d.marketCap ? d.marketCap / d.currentPrice : null;
  const targetEv = d.evEbitdaMultiple != null && d.fwdEbitdaRaw != null
    ? d.evEbitdaMultiple * d.fwdEbitdaRaw : null;
  const targetEquity = targetEv != null
    ? targetEv - (d.fwdNetDebtRaw ?? 0) : null;

  return (
    <div
      className="fixed inset-0 z-[3000] flex items-center justify-center"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60" />
      <div
        className="relative z-10 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto rounded-xl border border-[#1e3a5f] bg-[#050d18] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-[#050d18] px-5 py-3 border-b border-[#1e293b] flex items-center justify-between">
          <div>
            <span className="text-[13px] font-bold text-[#e2e8f0]">{d.symbol}</span>
            <span className="text-[11px] text-[#64748b] ml-2">Target Price Derivation</span>
            <span className="text-[11px] text-[#475569] ml-2">trading {fmt$(d.currentPrice, 2)}</span>
          </div>
          <div className="flex items-center gap-3">
            {d.consensusFairValue != null && (
              <div className="text-right">
                <div className="text-[11px] text-[#64748b]">Consensus</div>
                <div className="text-[16px] font-bold tabular-nums text-amber-300">
                  {fmt$(d.consensusFairValue, 2)}
                </div>
              </div>
            )}
            <button
              type="button"
              onClick={onClose}
              className="text-[#64748b] hover:text-white text-[16px] leading-none"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-5 space-y-3">
          {/* Method coverage bar */}
          {totalW > 0 && (
            <div className="flex items-center gap-2 mb-3">
              <div className="flex h-1.5 flex-1 rounded-full overflow-hidden gap-px">
                {availWeights.map((m) => (
                  <div
                    key={m.method}
                    style={{ flex: m.w / totalW, background: '#3b82f6' }}
                  />
                ))}
                {totalW < 1.0 && (
                  <div style={{ flex: 1 - totalW, background: '#1e293b' }} />
                )}
              </div>
              <span className="text-[9px] text-[#475569]">
                {availWeights.length}/4 methods · {(totalW * 100).toFixed(0)}% coverage
              </span>
            </div>
          )}

          {/* P/E METHOD */}
          <MethodCard
            label="P/E — Peer Multiple"
            weight={W.pe}
            totalW={totalW}
            fv={d.todayFairValue}
            current={d.currentPrice}
            available={d.todayFairValue != null}
          >
            {d.targetMultiple && (
              <>
                <Row label="Peer median P/E" value={fmtX(d.targetMultiple.peerMedian)} />
                <Row label="Own 5yr median P/E" value={fmtX(d.targetMultiple.ownMedian)} />
                <Sep />
                <Row
                  label="Applied multiple (nearTerm)"
                  value={fmtX(d.targetMultiple.nearTerm)}
                  sub={d.targetMultiple.final !== d.targetMultiple.nearTerm
                    ? `(raw blend ${fmtX(d.targetMultiple.final)} before quality premium)`
                    : undefined}
                />
                <Row label="FY+1 EPS" value={fmt$(d.fy1Eps, 2)} />
                <Sep />
                <Row
                  label="= P/E Fair Value"
                  value={`${fmt$(d.todayFairValue, 2)}`}
                />
              </>
            )}
          </MethodCard>

          {/* EV/EBITDA METHOD */}
          <MethodCard
            label="EV/EBITDA — Peer Multiple"
            weight={W.eveb}
            totalW={totalW}
            fv={d.evEbitdaFairValue}
            current={d.currentPrice}
            available={d.evEbitdaFairValue != null}
          >
            {d.evEbitdaFairValue != null ? (
              <>
                <Row label="Peer median EV/EBITDA" value={fmtX(d.evEbitdaMultiple)} />
                <Row label="Fwd EBITDA (BBG BEST_EBITDA)" value={fmt$(d.fwdEbitdaRaw)} />
                <Row
                  label="Net debt (BBG BEST_NET_DEBT)"
                  value={fmt$(d.fwdNetDebtRaw)}
                  sub={d.fwdNetDebtRaw != null && d.fwdNetDebtRaw < 0 ? '← net cash' : undefined}
                />
                <Sep />
                <Row label="Target EV (multiple × EBITDA)" value={fmt$(targetEv)} />
                <Row
                  label="− Net Debt  = Target Equity"
                  value={fmt$(targetEquity)}
                />
                {sharesApprox && (
                  <Row label="Shares (mktCap ÷ price)" value={`${(sharesApprox / 1e9).toFixed(2)}B`} />
                )}
                <Sep />
                <Row label="= EV/EBITDA Fair Value" value={fmt$(d.evEbitdaFairValue, 2)} />
              </>
            ) : (
              <div className="text-[10px] text-[#475569] italic">
                Not computed — {d.peerGroup === 'financial' || d.peerGroup === 'reit'
                  ? 'banks/REITs use specialized metrics'
                  : 'missing fwd EBITDA data'}
              </div>
            )}
          </MethodCard>

          {/* DCF METHOD */}
          <MethodCard
            label="DCF — Two-Stage Owner Earnings"
            weight={W.dcf}
            totalW={totalW}
            fv={d.dcfFairValue}
            current={d.currentPrice}
            available={d.dcfFairValue != null}
          >
            {d.dcfFairValue != null && d.dcfBreakdown ? (
              <>
                <Row label="Owner earnings / share" value={fmt$(d.ownerEarningsPerShare, 2)} />
                <Row
                  label="Discount rate (WACC)"
                  value={d.dcfDiscountRate != null ? fmtPct(d.dcfDiscountRate, 1).replace('+', '') : '—'}
                />
                {d.costOfEquityBreakdown && (
                  <>
                    <Row label="  Risk-free rate" value={fmtPct(d.costOfEquityBreakdown.riskFreeRate).replace('+', '')} />
                    <Row label="  β × ERP" value={
                      d.costOfEquityBreakdown.beta != null && d.costOfEquityBreakdown.equityRiskPremium != null
                        ? `${d.costOfEquityBreakdown.beta.toFixed(2)} × ${fmtPct(d.costOfEquityBreakdown.equityRiskPremium).replace('+', '')}`
                        : '—'
                    } />
                    {d.costOfEquityBreakdown.sizePremium != null && (
                      <Row label="  Size premium" value={fmtPct(d.costOfEquityBreakdown.sizePremium)} />
                    )}
                    {d.costOfEquityBreakdown.notes && d.costOfEquityBreakdown.notes.length > 0 && (
                      <div className="text-[9px] text-[#334155] italic mt-0.5 ml-2">
                        {d.costOfEquityBreakdown.notes[0]}
                      </div>
                    )}
                  </>
                )}
                <Sep />
                <Row label="Stage 1 PV (5yr)" value={fmt$(d.dcfBreakdown.stageOneSum, 2)} />
                <Row label="Terminal value" value={fmt$(d.dcfBreakdown.terminalValue, 2)} />
                <Row label="Terminal PV" value={fmt$(d.dcfBreakdown.terminalPv, 2)} />
                <Sep />
                <Row label="= DCF Fair Value" value={fmt$(d.dcfFairValue, 2)} />
                {d.currentPrice && d.dcfFairValue < d.currentPrice * 0.2 && (
                  <div className="text-[9px] text-amber-500/80 mt-1 italic">
                    Low DCF common for capex-intensive names — owner earnings understates cash generation
                  </div>
                )}
              </>
            ) : (
              <div className="text-[10px] text-[#475569] italic">No owner earnings data available</div>
            )}
          </MethodCard>

          {/* P/FCF METHOD */}
          <MethodCard
            label="P/FCF — Peer Multiple"
            weight={W.pfcf}
            totalW={totalW}
            fv={d.pfcfFairValue}
            current={d.currentPrice}
            available={d.pfcfFairValue != null}
          >
            {d.pfcfFairValue != null ? (
              <>
                <Row label="Peer median P/FCF" value={fmtX(d.pfcfMultiple)} />
                <Row label="TTM free cash flow" value={fmt$(d.fcfRaw)} />
                <Row label="TTM FCF per share" value={fmt$(d.fcfPerShare, 2)} />
                <Sep />
                <Row label="= P/FCF Fair Value" value={fmt$(d.pfcfFairValue, 2)} />
              </>
            ) : (
              <div className="text-[10px] text-[#475569] italic">
                Not computed — {d.peerGroup === 'financial' || d.peerGroup === 'reit'
                  ? 'banks/REITs use specialized metrics'
                  : 'no positive FCF data'}
              </div>
            )}
          </MethodCard>

          {/* Consensus summary */}
          {d.consensusFairValue != null && availWeights.length > 0 && (
            <div className="rounded-lg border border-amber-800/40 bg-amber-950/20 p-3">
              <div className="text-[10px] font-bold text-amber-300 uppercase tracking-wider mb-2">
                Consensus Weighted Blend
              </div>
              {availWeights.map((m) => (
                <div key={m.method} className="flex items-baseline justify-between gap-2 py-0.5">
                  <span className="text-[10px] text-[#64748b]">
                    {m.method === 'pe' ? 'P/E' : m.method === 'eveb' ? 'EV/EBITDA' : m.method === 'dcf' ? 'DCF' : 'P/FCF'}
                    {' '}({(m.w / totalW * 100).toFixed(0)}% normalized)
                  </span>
                  <span className="text-[10px] font-mono text-[#94a3b8]">
                    {fmt$(m.fv, 2)} × {(m.w / totalW).toFixed(2)} = {fmt$(m.fv * m.w / totalW, 2)}
                  </span>
                </div>
              ))}
              <div className="border-t border-amber-800/30 mt-2 pt-2 flex items-baseline justify-between">
                <span className="text-[11px] font-bold text-amber-300">Consensus Fair Value</span>
                <span className="text-[13px] font-bold font-mono tabular-nums text-amber-300">
                  {fmt$(d.consensusFairValue, 2)}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
